import client from "../config/api.config";
import i18n from "../i18n";
import { Appointment } from "../types/appointment.definition";
import { Availability } from "../types/availability.definition";
import {
  Doctor,
  DoctorFilters,
  DoctorSettingsInfo,
  DoctorStatsRequest,
} from "../types/doctor";
import { DoctorInvoice, DoctorInvoicePage } from "../types/doctorInvoice";
import {
  DoctorLicence,
  DoctorLicenceBillingDetail,
  DoctorLicencePlan,
} from "../types/licence";
import {
  AppointmentListFilters,
  DEFAULT_PAGE_SIZE,
  PageRequest,
} from "../types/pagination.definition";

// Use a function to get the client instead of initializing it at the module level
const getWebClient = () => client.apiClient;

export function getDoctorById(doctorId: number) {
  return getWebClient().request({
    method: "GET",
    url: `/doctors/${doctorId}`,
  });
}

export function updateDoctor(doctorId: number, data: Partial<Doctor>) {
  return getWebClient().request({
    method: "PUT",
    url: `/doctors/${doctorId}`,
    data,
  });
}

export function fetchAvailableDoctors(data: DoctorFilters) {
  return getWebClient().request({
    method: "GET",
    url: `/doctors/available`,
    data,
  });
}

/** One page (MED-1738): the response carries the full count in X-Total-Count. */
export function getAppointments(
  doctorId: string,
  {
    page = 0,
    size = DEFAULT_PAGE_SIZE,
    ...filters
  }: PageRequest & AppointmentListFilters = {}
) {
  return getWebClient().request<Appointment[]>({
    method: "GET",
    url: `/doctors/${doctorId}/appointments`,
    params: { page, size, ...filters },
  });
}

// Get all available time slots for a doctor
export function getAvailableTimeSlots(doctorId: number) {
  const result = getWebClient().request({
    method: "GET",
    url: `/doctor-availability/${doctorId}/available-time-slots`,
  });
  return result;
}

// Type for the API request payload
interface CreateTimeSlotsPayload {
  daysOfWeek: string[];
  startDate: string;
  endDate?: string;
  startTime: string;
  endTime: string;
  isRecurring: boolean;
  slotDurationMinutes?: number;
  appointmentTime?: string;
  // Location: either healthcareFacilityId or the manual address fields must be provided
  healthcareFacilityId?: number;
  address?: string;
  city?: string;
  country?: string;
  state?: string;
  zip?: string;
  accessInformation?: string;
}

// Create time slots for a doctor
export function createTimeSlots(
  doctorId: number,
  data: Availability & { slotDurationMinutes?: number },
  includeAppointmentTime: boolean = false
) {
  const transformedData: CreateTimeSlotsPayload = {
    daysOfWeek: data.daysOfWeek || (data.dayOfWeek ? [data.dayOfWeek] : []),
    startDate: data.startDate,
    endDate: data.endDate,
    startTime: data.startTime,
    endTime: data.endTime,
    isRecurring: data.isRecurring,
    slotDurationMinutes: data.slotDurationMinutes,
    healthcareFacilityId: data.healthcareFacilityId,
    address: data.address,
    city: data.city,
    country: data.country,
    state: data.state,
    // MED-1724: the postal code is optional; a blank input is sent as absent, not ""
    zip: data.zip?.trim() || undefined,
    accessInformation: data.accessInformation,
  };

  // Only include appointmentTime if explicitly requested
  if (includeAppointmentTime && data.startDate && data.startTime) {
    // Ensure time is in HH:mm format (no seconds, no timezone)
    // If time already has seconds, remove them
    const timePart = data.startTime.includes(":")
      ? data.startTime.split(":").slice(0, 2).join(":")
      : data.startTime;
    transformedData.appointmentTime = `${data.startDate}T${timePart}:00+00:00`;
  }
  const result = getWebClient().request({
    method: "POST",
    url: `/doctor-availability/${doctorId}`,
    data: transformedData,
  });
  return result;
}

// Delete a time slot
export function deleteTimeSlot(doctorId: number, timeSlotId: number) {
  return getWebClient().request({
    method: "DELETE",
    url: `/doctor-availability/${doctorId}/time-slot/${timeSlotId}`,
  });
}

// Get available time slots by date range
export function getAvailableTimeSlotsByDateRange(
  doctorId: number,
  startDate: string,
  endDate: string
) {
  return getWebClient().request({
    method: "GET",
    url: `/doctor-availability/${doctorId}/available-time-slots/range?startDate=${startDate}&endDate=${endDate}`,
  });
}

// Get available time slots by date
export function getAvailableTimeSlotsByDate(doctorId: number, date: string) {
  return getWebClient().request({
    method: "GET",
    url: `/doctor-availability/${doctorId}/available-time-slots/date/${date}`,
  });
}

// Get all time slots by date (includes all statuses: available, booked, cancelled, expired)
export function getAllTimeSlotsByDate(doctorId: number, date: string) {
  return getWebClient().request({
    method: "GET",
    url: `/doctor-availability/${doctorId}/time-slots/date/${date}`,
  });
}

// Get all time slots by date range (includes all statuses: available, booked, cancelled, expired)
export function getAllTimeSlotsByDateRange(
  doctorId: number,
  startDate: string,
  endDate?: string | null
) {
  const url = endDate
    ? `/doctor-availability/${doctorId}/time-slots/range?startDate=${startDate}&endDate=${endDate}`
    : `/doctor-availability/${doctorId}/time-slots/range?startDate=${startDate}`;
  return getWebClient().request({
    method: "GET",
    url,
  });
}

// Delete time slots by date range (returns booked slots that cannot be deleted)
export function deleteTimeSlotsByDateRange(
  doctorId: number,
  startDate: string,
  endDate: string
) {
  const url = `/doctor-availability/${doctorId}/time-slots/range?startDate=${startDate}&endDate=${endDate}`;
  return getWebClient().request({
    method: "DELETE",
    url,
  });
}

export function getDoctorAvailabilities(doctorId: number) {
  return getAvailableTimeSlots(doctorId);
}

export function getDoctorAvailabilitiesInRange(
  doctorId: number,
  startDate?: string,
  endDate?: string
) {
  if (startDate && endDate) {
    return getAvailableTimeSlotsByDateRange(doctorId, startDate, endDate);
  }
  return getAvailableTimeSlots(doctorId);
}

export function addDoctorAvailability(doctorId: number, data: Availability) {
  // Don't include appointmentTime when adding from ManageSchedule
  return createTimeSlots(doctorId, data, false);
}

export function deleteDoctorTimeSlotAvailability(
  doctorId: number,
  _availabilityId: number,
  timeSlotId: number
) {
  return deleteTimeSlot(doctorId, timeSlotId);
}

export function deleteDoctorAvailability(
  doctorId: string,
  availabilityId: string
) {
  return getWebClient().request({
    method: "DELETE",
    url: `/doctor-availability/${doctorId}/availability/${availabilityId}`,
  });
}

export function updateAppointment(appointmentId: number, data: Appointment) {
  return getWebClient().request({
    method: "PUT",
    url: `/appointments/${appointmentId}`,
    data,
  });
}

export function cancelAppointment(appointmentId: string) {
  return getWebClient().request({
    method: "PUT",
    url: `/appointments/cancel/${appointmentId}`,
    // Language picked in the header switcher — drives the cancellation email
    params: { lang: i18n.language || "fr" },
  });
}

export function updateDoctorSettings(
  doctorId: number,
  data: DoctorSettingsInfo
) {
  return getWebClient().request({
    method: "PUT",
    url: `/doctors/${doctorId}/settings`,
    data,
  });
}

export function getPatientById(patientId: number) {
  return getWebClient().request({
    method: "GET",
    url: `/doctors/patients/${patientId}`,
  });
}

export function getDoctorPatients(doctorId: number) {
  return getWebClient().request({
    method: "GET",
    url: `/doctors/${doctorId}/patients`,
  });
}

export function getDoctorPatientDetails(doctorId: number, patientId: number) {
  return getWebClient().request({
    method: "GET",
    url: `/doctors/${doctorId}/patients/${patientId}`,
  });
}

export function getDoctorAppointmentStats(
  doctorId: number,
  data: DoctorStatsRequest
) {
  return getWebClient().request({
    method: "POST",
    url: `/doctors/${doctorId}/stats`,
    data,
  });
}

export function getAllSpecialties(language?: string) {
  // Get language from parameter, i18n, or default to 'fr'
  const lang = language || i18n.language || "fr";
  const currentLocale = lang === "fr" ? "fr-FR" : "en-US";

  return getWebClient().request({
    method: "GET",
    url: `/specialties`,
    headers: {
      "Accept-Language": currentLocale,
    },
  });
}

export function getLicencePlans() {
  return getWebClient().request({
    method: "GET",
    url: "/licences/plans",
  });
}

export function purchaseDoctorLicence(
  planType: string,
  contractDuration: string,
  whatsappDiscount?: boolean
) {
  const data: Record<string, unknown> = { planType, contractDuration };
  if (whatsappDiscount !== undefined) {
    // Backend accepts snake_case for this optional flag.
    data.whatsapp_discount = whatsappDiscount;
  }
  return getWebClient().request({
    method: "POST",
    url: "/doctors/me/licence",
    data,
  });
}

/**
 * Fetch itemized billing lines for a specific doctor licence.
 * Response payload uses snake_case keys — see {@link DoctorLicenceBillingDetail}.
 */
export function getDoctorLicenceBillingDetail(
  doctorId: number,
  licenceId: number
): Promise<DoctorLicenceBillingDetail> {
  return getWebClient()
    .request<DoctorLicenceBillingDetail>({
      method: "GET",
      url: `/doctors/${doctorId}/licences/${licenceId}/billing_detail`,
    })
    .then(res => res.data);
}

/** MED-1686: the logged-in doctor's licence invoices, newest first. */
export function getMyLicenceInvoices(
  page = 0,
  size = 20
): Promise<DoctorInvoicePage> {
  return getWebClient()
    .request<DoctorInvoicePage>({
      method: "GET",
      url: "/doctors/me/licence/invoices",
      params: { page, size },
    })
    .then(res => res.data);
}

/** MED-1686: one invoice of the logged-in doctor's licence. */
export function getMyLicenceInvoice(invoiceId: number): Promise<DoctorInvoice> {
  return getWebClient()
    .request<DoctorInvoice>({
      method: "GET",
      url: `/doctors/me/licence/invoices/${invoiceId}`,
    })
    .then(res => res.data);
}

/** Cancel (remove) a doctor licence. */
export function cancelDoctorLicence(
  doctorId: number,
  licenceId: number
): Promise<void> {
  return getWebClient()
    .request<void>({
      method: "POST",
      url: `/doctors/${doctorId}/licences/${licenceId}/cancel`,
    })
    .then(() => undefined);
}

export function getDoctorLicence(): Promise<DoctorLicence> {
  return getWebClient()
    .request<DoctorLicence>({
      method: "GET",
      url: "/doctors/me/licence",
    })
    .then(res => res.data);
}

export function getDoctorLicencePlans(
  lang: string
): Promise<DoctorLicencePlan[]> {
  return getWebClient()
    .request<DoctorLicencePlan[]>({
      method: "GET",
      url: "/doctors/licence-plans",
      params: { lang },
    })
    .then(res => res.data);
}

export function upgradeDoctorLicence(
  doctorId: number,
  licenceId: number,
  planType: string,
  contractDuration: string
): Promise<DoctorLicence> {
  return getWebClient()
    .request<DoctorLicence>({
      method: "PUT",
      url: `/doctors/${doctorId}/licences/${licenceId}`,
      data: { planType, contractDuration },
    })
    .then(res => res.data);
}

/**
 * Renew the doctor's ended annual contract for another year, keeping or
 * changing the plan. The API refuses it (DOCTOR_LICENCE_RENEWAL_NOT_DUE)
 * while the contract still covers the period to pay.
 */
export function renewDoctorLicence(planType: string): Promise<DoctorLicence> {
  return getWebClient()
    .request<DoctorLicence>({
      method: "POST",
      url: "/doctors/me/licence/renew",
      data: { planType },
    })
    .then(res => res.data);
}
